import { Card, CardBody, CardHeader, Colors, Icon, Text } from '@amelio/ui-library';

import type { ScheduleSummaryRow } from './LaunchSummary.types';

import { useAmelioTranslations } from '@/hooks';
import {
    CYCLE_CARD_SURFACE_BG_COLOR,
    CYCLE_CARD_SURFACE_BORDER_COLOR,
    CYCLE_SECTION_SURFACE,
} from '@/pages/CycleEvaluation/components/cycleCardSurface';

export interface ScheduleCardProps {
    schedule: ScheduleSummaryRow[];
}

export const ScheduleCard = ({ schedule }: ScheduleCardProps) => {
    const { t } = useAmelioTranslations();

    return (
        <Card
            padding={'none'}
            className={CYCLE_SECTION_SURFACE}
            bgColor={CYCLE_CARD_SURFACE_BG_COLOR}
            borderColor={CYCLE_CARD_SURFACE_BORDER_COLOR}
            dataTestId={'launch-summary-schedule'}
        >
            <CardHeader
                icon={
                    <Icon
                        name={'messages-sms'}
                        color={Colors.STEP_RAIL_TURQUOISE}
                        size={16}
                    />
                }
                title={t('cycleEvaluationSchedule.launchSummary.schedule.title')}
            />

            <CardBody>
                <ul className={'flex flex-col gap-3'}>
                    {schedule.map((row) => (
                        <li
                            key={row.id}
                            className={[
                                'flex items-start gap-3 rounded-lg border p-3',
                                row.isBlocked
                                    ? 'border-red-300 bg-red-50'
                                    : 'border-slate-100 bg-white/80',
                            ].join(' ')}
                            data-testid={`schedule-row-${row.id}`}
                        >
                            <Icon
                                name={'messages-sms'}
                                color={Colors.STEP_RAIL_TURQUOISE}
                                size={16}
                            />

                            <div>
                                <Text weight={'semibold'}>{t(row.labelKey)}</Text>

                                <Text
                                    color={Colors.STEP_RAIL_MUTED}
                                    className={'mt-[2px] text-xs'}
                                >
                                    {row.dateTimeLabel}
                                </Text>

                                <Text className={'mt-[4px] text-xs'}>
                                    {row.audienceLabels.length > 0
                                        ? t(
                                              'cycleEvaluationSchedule.launchSummary.schedule.willSendTo',
                                              {
                                                  audiences: row.audienceLabels.join(', '),
                                              },
                                          )
                                        : t(
                                              'cycleEvaluationSchedule.launchSummary.schedule.noEmail',
                                          )}
                                </Text>

                                {row.isBlocked && (
                                    <div
                                        className={'mt-1 flex items-center gap-1.5'}
                                        data-testid={`schedule-row-${row.id}-blocked`}
                                    >
                                        <Icon
                                            name={'essential-warning-2'}
                                            color={Colors.ERROR}
                                            size={14}
                                        />

                                        <Text
                                            color={Colors.ERROR}
                                            className={'text-xs'}
                                        >
                                            {t(
                                                'cycleEvaluationSchedule.launchSummary.schedule.itemBlocking',
                                            )}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </CardBody>
        </Card>
    );
};
